// Helpers for the linked Location / LSD pickers backed by customer.sites [{id, name, lsd}].
const uniq = (vals) => [...new Set(vals.filter(Boolean))];
const same = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

export const normalizedSiteValue = (value) => String(value || '').trim().toLowerCase();
export const sameSiteLocation = (a, b) => same(a.name, b.name) && same(a.lsd, b.lsd)
  && (Boolean(normalizedSiteValue(a.lsd)) || same(a.address, b.address));
export const groupSites = (sites = []) => {
  const groups = new Map();
  sites.forEach((site) => {
    const key = normalizedSiteValue(site.name);
    if (!groups.has(key)) groups.set(key, { key, name: site.name?.trim() || '', sites: [] });
    groups.get(key).sites.push(site);
  });
  return [...groups.values()].map((g) => ({ ...g,
    lsdCount: new Set(g.sites.filter((s) => s.lsd).map((s) => normalizedSiteValue(s.lsd))).size,
    addressCount: new Set(g.sites.filter((s) => !s.lsd && s.address).map((s) => normalizedSiteValue(s.address))).size,
  }))
    .sort((a, b) => !a.name ? -1 : !b.name ? 1 : a.name.localeCompare(b.name));
};

export const siteNames = (sites = []) => uniq(sites.map((s) => s.name));
export const siteLsds = (sites = []) => uniq(sites.map((s) => s.lsd));

// Picking a location: keep the current LSD if it belongs to that site, otherwise jump to the site's LSD.
export const lsdForName = (sites = [], name, currentLsd = '') => {
  const matches = sites.filter((s) => same(s.name, name));
  if (!matches.length || matches.some((s) => same(s.lsd, currentLsd))) return currentLsd;
  return matches[0].lsd || currentLsd;
};

export const nameForLsd = (sites = [], lsd, currentName = '') => {
  const matches = sites.filter((s) => same(s.lsd, lsd));
  if (!matches.length || matches.some((s) => same(s.name, currentName))) return currentName;
  return matches[0].name || currentName;
};

// Add a typed name/LSD, filling in the blank half of an existing site when possible.
export const mergeSiteValue = (sites = [], entry) => {
  let { name = '', lsd = '' } = entry;
  name = name.trim(); lsd = lsd.trim();
  const exact = sites.find((s) => sameSiteLocation(s, { ...entry, name, lsd }));
  if (exact) return sites.map((s) => s === exact ? { ...s, ...entry, id: s.id, name, lsd } : s);
  if (name && lsd) {
    const half = sites.find((s) => (same(s.lsd, lsd) && !s.name) || (same(s.name, name) && !s.lsd && !s.address));
    if (half) return sites.map((s) => (s.id === half.id ? { ...s, ...entry, id: s.id, name, lsd } : s));
  }
  return [...sites, { ...entry, id: crypto.randomUUID(), name, lsd }];
};
